import { Popover } from "@circleco/compass/components/Popover";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import type { ModerationActionOptions } from "@/react/components/SettingsApp/Moderation/useModerationApi";
import type { FlaggedContentableType } from "@/react/types";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  CONNECT_CONNECTION_TYPE,
} from "@/react/types/FlaggedContent";

import { ActionSplitButton } from "./ActionSplitButton";
import type { ActionOption } from "./ActionSplitButton";
import { RejectReasonDrawer } from "./RejectReasonDrawer";
import { RejectReasonPopoverContent } from "./RejectReasonPopoverContent";

interface ApproveRejectActionsProps {
  i18nRoot: string;
  isApproving: boolean;
  isAuthorFlagged?: boolean;
  isEmbeddedChat?: boolean;
  isFlaggedForConnect?: boolean;
  isRejecting?: boolean;
  isRejectWithReason?: boolean;
  onApprove: (options?: ModerationActionOptions) => void;
  onDismissRejectWithReason?: () => void;
  onReject: (options?: ModerationActionOptions) => void;
  onRejectWithReason?: (reason: string | null) => void;
  status: string | null;
  type: FlaggedContentableType;
  useRejectReasonPopover?: boolean;
}

interface ConditionalOption {
  isVisible: boolean;
  labelKey: string;
  moderationOptions: ModerationActionOptions;
  value: string;
}

const buildDropdownOptions = (
  i18nRoot: string,
  primaryLabelKey: string,
  primaryValue: string,
  onAction: (options?: ModerationActionOptions) => void,
  conditionalOptions: ConditionalOption[],
): ActionOption[] => [
  {
    label: t([i18nRoot, primaryLabelKey]),
    value: primaryValue,
    onClick: () => onAction(),
  },
  ...conditionalOptions
    .filter(({ isVisible }) => isVisible)
    .map(({ labelKey, value, moderationOptions }) => ({
      label: t([i18nRoot, labelKey]),
      value,
      onClick: () => onAction(moderationOptions),
    })),
];

export const ApproveRejectActions = ({
  i18nRoot,
  isApproving,
  isAuthorFlagged,
  isEmbeddedChat,
  isFlaggedForConnect,
  isRejecting = false,
  isRejectWithReason = false,
  onApprove,
  onDismissRejectWithReason,
  onReject,
  onRejectWithReason,
  status,
  type,
  useRejectReasonPopover = false,
}: ApproveRejectActionsProps) => {
  const [approveSelectedAction, setApproveSelectedAction] = useState<
    ActionOption | undefined
  >();
  const [rejectSelectedAction, setRejectSelectedAction] = useState<
    ActionOption | undefined
  >();
  const rejectAnchorRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const isConnection = type === CONNECT_CONNECTION_TYPE;
  const isPending = status === null;
  const shouldShowApprove = status === null || status === "rejected";
  const shouldShowReject = status === null || status === "approved";

  const isChatMessage = type === CHAT_ROOM_MESSAGE_TYPE;

  const approveOptions = useMemo(
    () =>
      buildDropdownOptions(i18nRoot, "approve", "approve", onApprove, [
        {
          isVisible: isPending && isConnection && !isFlaggedForConnect,
          labelKey: "approve_and_flag_for_connect",
          value: "approve_flag_connect",
          moderationOptions: { flagForConnect: true },
        },
        {
          isVisible:
            isPending && !isConnection && !isChatMessage && !isAuthorFlagged,
          labelKey: "approve_and_flag_member",
          value: "approve_flag_member",
          moderationOptions: { flagMember: true },
        },
      ]),
    [
      i18nRoot,
      isConnection,
      isChatMessage,
      isPending,
      isFlaggedForConnect,
      isAuthorFlagged,
      onApprove,
    ],
  );

  const rejectOptions = useMemo(
    () =>
      buildDropdownOptions(i18nRoot, "reject", "reject", onReject, [
        {
          isVisible: isPending && isConnection && !isFlaggedForConnect,
          labelKey: "remove_and_flag_for_connect",
          value: "remove_flag_connect",
          moderationOptions: { flagForConnect: true },
        },
        {
          isVisible: isPending && isConnection,
          labelKey: "remove_and_disable_connections",
          value: "remove_disable_connections",
          moderationOptions: { disableConnections: true },
        },
        {
          isVisible: Boolean(isPending && isChatMessage && isEmbeddedChat),
          labelKey: "remove_and_disable_chat_messaging",
          value: "remove_disable_chat_messaging",
          moderationOptions: { flagForChat: true },
        },
        {
          isVisible: Boolean(isPending && isChatMessage && !isEmbeddedChat),
          labelKey: "remove_and_disable_direct_messaging",
          value: "remove_disable_direct_messaging",
          moderationOptions: { disableDirectMessaging: true },
        },
        {
          isVisible:
            isPending && !isConnection && !isChatMessage && !isAuthorFlagged,
          labelKey: "remove_and_flag_member",
          value: "remove_flag_member",
          moderationOptions: { flagMember: true },
        },
      ]),
    [
      i18nRoot,
      isConnection,
      isChatMessage,
      isEmbeddedChat,
      isPending,
      isFlaggedForConnect,
      isAuthorFlagged,
      onReject,
    ],
  );

  return (
    <div className="flex items-center justify-end gap-2">
      {shouldShowApprove && (
        <ActionSplitButton
          disabled={isApproving}
          options={approveOptions}
          selectedAction={approveSelectedAction}
          setSelectedAction={setApproveSelectedAction}
          variant="secondary"
        />
      )}
      {shouldShowReject &&
        (useRejectReasonPopover ? (
          <>
            <div ref={rejectAnchorRef}>
              <ActionSplitButton
                options={rejectOptions}
                selectedAction={rejectSelectedAction}
                setSelectedAction={setRejectSelectedAction}
                variant="secondary"
              />
            </div>
            {isMobile ? (
              onRejectWithReason && (
                <RejectReasonDrawer
                  i18nRoot={i18nRoot}
                  isOpen={isRejectWithReason}
                  isRejecting={isRejecting}
                  onDismiss={onDismissRejectWithReason}
                  onSubmit={onRejectWithReason}
                />
              )
            ) : (
              <Popover
                anchor={rejectAnchorRef}
                open={isRejectWithReason}
                onOpenChange={open => {
                  if (!open) {
                    onDismissRejectWithReason?.();
                  }
                }}
                side="bottom"
                align="end"
                sideOffset={8}
                className="w-[280px] p-4"
              >
                {onRejectWithReason && (
                  <RejectReasonPopoverContent
                    i18nRoot={i18nRoot}
                    isRejecting={isRejecting}
                    onSubmit={onRejectWithReason}
                    rejectLabel={t([i18nRoot, "reject"])}
                  />
                )}
              </Popover>
            )}
          </>
        ) : (
          <ActionSplitButton
            options={rejectOptions}
            selectedAction={rejectSelectedAction}
            setSelectedAction={setRejectSelectedAction}
            variant="danger"
          />
        ))}
    </div>
  );
};
